import React, {
  useCallback,
  useContext, useEffect, useMemo, useState,
} from 'react';
import {
  Checkbox, CountrySelector, FlagBlock, HeadingBanner, Link,
} from 'anf-core-react';
import { useMutation } from '@apollo/client';
import { Controller, useFormContext } from 'react-hook-form';
import CheckoutPageContext from '../../../context/CheckoutPageContext';
import AddressForm from '../../Common/AddressForm/AddressForm';
import Tmnt from '../../Tmnt/Tmnt';
import AddressSelectionModal from './AddressSelectionModal/AddressSelectionModal';
import SavedAddress from './SavedAddress/SavedAddress';
import SHIPPING_SPEED_UPDATE_MUTATION from '../../../gql/shippingSpeed.gql';
import useLog from '../../useLog/useLog';
import $document from '../../../tools/document';
import { isPageUrlContains } from '../../../tools/pageHelper';
import { AddressEvents } from '../../../tools/address';
import { useStepsContext } from '../../Steps/StepsContext';
import { validatePostalCode } from '../../../tools/validations/address';
import $window from '../../../tools/window';

const SHIPPING_VIEW = {
  SHIPPING_FORM: 'SHIPPING_FORM',
  SAVED_ADDRESS: 'SAVED_ADDRESS',
};

export default function ShippingAddressContainer() {
  const [visibleView, setVisibleView] = useState(SHIPPING_VIEW.SAVED_ADDRESS);
  const [openAddressSelectionModal, setOpenAddressSelectionModal] = useState(false);
  const [isNewAddressSelected, setIsNewAddressSelected] = useState(false);
  const [isAddressySelected, setIsAddressySelected] = useState(false);
  const [isAddressVerified, setIsAddressVerified] = useState(false);
  const {
    control, getValues, setError, reset,
  } = useFormContext();

  const {
    checkoutPageState,
    setCheckoutPageState,
    setIsUpdating,
  } = useContext(CheckoutPageContext);

  const {
    textFor,
    addressBook,
    shippingAddressConfig,
    shipOrPickup,
    addressyApiKey,
    shippingAddress,
    userData,
  } = checkoutPageState;

  const logger = useLog('checkout.updateAddressType');
  const isPayPalExpressCheckout = isPageUrlContains('/ProcessPayPalReturnAndOrderExpress');

  const [updateAddressMutation] = useMutation(SHIPPING_SPEED_UPDATE_MUTATION, {
    onCompleted: (response) => {
      logger.debug('SHIPPING_SPEED_UPDATE_MUTATION_FROM_UPDATE_ADDRESS_RESULT', response);
      if (response.updateShippingSpeed.statusCode === '200') {
        setCheckoutPageState((currentState) => ({
          ...currentState, ...response.updateShippingSpeed,
        }));
      } else if (response.updateShippingSpeed.statusCode === '400') {
        const statusMessages = response.updateShippingSpeed.statusMessages || [];
        statusMessages.forEach((statusMessage) => {
          switch (statusMessage.key) {
            case '_ERR_STATE_ZIP_INVALID.taxGeoCode':
              setError('addresses.homeDelivery.postalCode', { message: 'zipCodeStateNotMatch', type: 'custom' }, { shouldFocus: true });
              break;
            case '_ERR_COUNTRY_ZIP_INVALID':
              setError('addresses.homeDelivery.postalCode', { message: 'enterValidField', type: 'custom' }, { shouldFocus: true });
              break;
            default:
              break;
          }
        });
      }
      setIsUpdating(false);
    },
    onError: (err) => {
      setIsUpdating(false);
      logger.error(`ERR: ADDRESS_UPDATE_MUTATION: ${JSON.stringify(err)}`);
    },
  });

  const setModalVisibility = (isVisible) => {
    setOpenAddressSelectionModal(isVisible);
    $document.documentElement.setAttribute('data-scroll-lock', 'false');
  };

  const onShippingAddressChanged = () => {
    setIsAddressVerified(false);
  };

  const onAddNewAddress = () => {
    setModalVisibility(false);
    setVisibleView(SHIPPING_VIEW.SHIPPING_FORM);
    reset((prev) => ({
      ...prev,
      defaultShippingAddressOption: userData?.isLoggedIn,
      addresses: {
        ...prev.addresses,
        homeDelivery: {
          ...prev.addresses?.homeDelivery,
          address1: '',
          address2: '',
          city: '',
          firstName: '',
          lastName: '',
          poBox: '',
          postalCode: '',
        },
      },
    }));
    setIsNewAddressSelected(true);
  };

  const isStateAvailable = useMemo(
    () => !!shippingAddressConfig?.addressFields?.find((item) => item.name === 'addresses.homeDelivery.state'),
    [shippingAddressConfig],
  );

  const onUseThisAddress = async (selectedAddress, isSelectedAddress) => {
    setIsNewAddressSelected(false);
    if (isSelectedAddress) {
      setModalVisibility(false);
      return;
    }
    setIsUpdating(true);
    setModalVisibility(false);
    await updateAddressMutation({
      variables: {
        shippingAddress: {
          address1: selectedAddress.address1,
          address2: selectedAddress.address2,
          addressId: selectedAddress.addressId,
          addressType: selectedAddress.addressType,
          city: selectedAddress.city,
          country: selectedAddress.country,
          displayType: selectedAddress.displayType,
          firstName: selectedAddress.firstName,
          isBillingAllowed: selectedAddress.isBillingAllowed,
          isDefaultBilling: selectedAddress.isDefaultBilling,
          isDefaultShipping: selectedAddress.isDefaultShipping,
          isShippingAllowed: selectedAddress.isShippingAllowed,
          lastName: selectedAddress.lastName,
          orgName: selectedAddress.orgName,
          pickUpStoreId: selectedAddress.pickUpStoreId,
          poBox: selectedAddress.poBox,
          postalCode: selectedAddress.postalCode,
          state: selectedAddress.state,
          subType: selectedAddress.subType,
          title: selectedAddress.title,
        },
      },
    });
    setVisibleView(SHIPPING_VIEW.SAVED_ADDRESS);
  };

  const addressId = useMemo(
    () => checkoutPageState?.shippingAddress?.addressId,
    [checkoutPageState?.shippingAddress,
    ],
  );

  const onUpdateAddress = useCallback((address) => {
    setIsUpdating(true);
    updateAddressMutation({
      variables: {
        shippingAddress: {
          address1: address?.address1,
          address2: address?.address2,
          city: address?.city,
          firstName: address?.firstName,
          lastName: address?.lastName,
          poBox: !!address?.poBox,
          postalCode: address?.postalCode,
          state: isStateAvailable ? address?.state : address?.country,
          country: address?.country,
          addressId,
        },
      },
    });
  }, [updateAddressMutation, setIsUpdating, isStateAvailable, addressId]);

  const onSelectAddress = (isSelected) => {
    setIsAddressySelected(isSelected);
    setIsAddressVerified(isSelected);

    if (!isSelected) return;
    const values = getValues();
    onUpdateAddress({ ...values?.addresses?.homeDelivery, country: shippingAddress?.country });
  };

  useEffect(() => {
    if (!isPayPalExpressCheckout && addressBook && !addressBook.defaultShipping) {
      setVisibleView(SHIPPING_VIEW.SHIPPING_FORM);
    }
  }, [addressBook, isPayPalExpressCheckout]);

  useEffect(() => {
    if (userData?.isLoggedIn) {
      reset((prev) => ({ ...prev, defaultShippingAddressOption: true }));
    } else {
      reset((prev) => ({ ...prev, defaultShippingAddressOption: false }));
    }
  }, [userData, reset]);

  useEffect(() => {
    if (shippingAddress) {
      reset((prev) => ({
        ...prev,
        addresses: {
          ...prev.addresses,
          homeDelivery: {
            ...prev.addresses.homeDelivery,
            ...shippingAddress,
          },
        },
      }));
    }
  }, [shippingAddress, reset]);

  const onStateSelected = useCallback((event) => {
    if (!isAddressySelected) return;

    const values = getValues();
    onUpdateAddress({
      ...values?.addresses?.homeDelivery,
      state: event.detail.state,
      country: shippingAddress?.country,
    });
  }, [isAddressySelected, getValues, onUpdateAddress, shippingAddress]);

  const onPostalCodeChanged = useCallback((event) => {
    if (!event?.detail?.postalCode) return;
    const isValid = validatePostalCode(
      event.detail.postalCode,
      shippingAddress?.country,
      shippingAddress?.isAPOFPO,
    );
    if (!isValid) return;
    const values = getValues();
    const updatedAddress = {
      ...values?.addresses?.homeDelivery,
      postalCode: event.detail.postalCode,
      country: shippingAddress?.country,
    };
    $window.dispatchEvent(
      new CustomEvent(
        AddressEvents.SDDAddressChanged,
        {
          detail: {
            addresses: {
              homeDelivery: updatedAddress,
            },
          },
        },
      ),
    );
    onUpdateAddress(updatedAddress);
  }, [getValues, onUpdateAddress, shippingAddress]);

  useEffect(() => {
    window.addEventListener(AddressEvents.ShippingStateSelected, onStateSelected);
    window.addEventListener(AddressEvents.ShippingPostalCodeChanged, onPostalCodeChanged);
    window.addEventListener(AddressEvents.ShippingAddressChanged, onShippingAddressChanged);

    return () => {
      window.removeEventListener(AddressEvents.ShippingStateSelected, onStateSelected);
      window.removeEventListener(AddressEvents.ShippingPostalCodeChanged, onPostalCodeChanged);
      window.removeEventListener(AddressEvents.ShippingAddressChanged, onShippingAddressChanged);
    };
  }, [onStateSelected, onPostalCodeChanged]);

  const addressConfigData = useMemo(() => {
    if (!shippingAddressConfig || shippingAddressConfig.addressFields.length === 0) {
      return null;
    }

    return {
      id: 'shipping',
      fields: shippingAddressConfig.addressFields.map((field) => {
        const label = textFor?.[field.label]
          ?? { key: field.label, value: field.label };
        const error = textFor?.[field.errorMessage]
          ?? { key: field.errorMessage, value: field.errorMessage };
        return {
          ...field,
          label,
          error,
        };
      }),
    };
  }, [shippingAddressConfig, textFor]);

  const onChangeAddress = () => {
    setOpenAddressSelectionModal(true);
  };

  const isSavedAddressVisible = useMemo(
    () => visibleView === SHIPPING_VIEW.SHIPPING_FORM
      && addressBook
      && addressBook.addresses?.length > 0,
    [addressBook, visibleView],
  );

  const savedAddress = useMemo(() => {
    if (isPayPalExpressCheckout) return shippingAddress;

    if (!addressBook || !addressBook.addresses) return null;

    const defaultAddress = addressBook.addresses?.find(
      (item) => item.addressId === shippingAddress?.addressId,
    );

    if (defaultAddress) return defaultAddress;

    return addressBook.addresses.find(
      (item) => item.addressId === addressBook.defaultShipping,
    );
  }, [addressBook, shippingAddress, isPayPalExpressCheckout]);

  const errorTmnts = useMemo(() => ({
    firstNameMissing: textFor?.firstNameMissing,
    lastNameMissing: textFor?.lastNameMissing,
    nameMissing: textFor?.nameMissing,
    address1ErrorMessage: textFor?.address1ErrorMessage,
    cityMissing: textFor?.cityMissing,
    postalUSCodeErrorMessage: textFor?.postalUSCodeErrorMessage,
    postalCACodeErrorMessage: textFor?.postalCACodeErrorMessage,
    postalCodeErrorMessage: textFor?.postalCodeErrorMessage,
    enterValidField: textFor?.enterValidField,
    zipCodeStateNotMatch: textFor?.zipCodeStateNotMatch,
  }), [textFor]);

  // Safely get step context - returns null if not within StepsProvider
  let currentStep = null;
  try {
    const stepsContext = useStepsContext();
    currentStep = stepsContext?.currentStep;
  } catch (error) {
    // Component is not within StepsProvider
    currentStep = null;
  }

  const onChange = () => {
    const shippingCountryEvent = new CustomEvent(
      'mfe:open:changeShippingCountryController',
    );
    $window.dispatchEvent(shippingCountryEvent);
  };

  const isExpressPayLane = shipOrPickup.isExpressLane;

  if (!addressConfigData) return null;

  return (
    <div className="shipping-address-container">
      {!currentStep ? (
        <HeadingBanner variant="boxed">
          <h2>
            <Tmnt tmnt={textFor?.shippingAddress} />
          </h2>
          {isSavedAddressVisible && (
          <button
            className="button link-button"
            onClick={onChangeAddress}
            type="button"
          >
            {textFor?.savedAddress?.value}
          </button>
          )}
        </HeadingBanner>
      ) : null}
      {currentStep ? (
        <div className="button-container">
          {!isExpressPayLane && checkoutPageState?.shipMethod?.isHomeDelivery && (
          <div className="country-section">
            <CountrySelector
              flagBlock={(
                <FlagBlock countryCode={shipOrPickup?.country} countryName={`${textFor?.shipTo?.value} ${shipOrPickup?.country}`} />
                )}
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
              link={<Link onClick={onChange}>{textFor?.change?.value}</Link>}
            />
          </div>
          )}
          {isSavedAddressVisible && (
          <button
            className="button link-button change-address-button"
            onClick={onChangeAddress}
            type="button"
          >
            {textFor?.savedAddress?.value}
          </button>
          )}
        </div>
      ) : null}
      {visibleView === SHIPPING_VIEW.SHIPPING_FORM && (
        <>
          <input type="hidden" value={addressId} name="addresses.homeDelivery.addressId" />
          <input type="hidden" value={isAddressVerified} name="addresses.homeDelivery.isAddressVerified" data-testid="isAddressVerified" />
          <AddressForm
            addressConfig={addressConfigData}
            type="shipping"
            fieldInFix="homeDelivery"
            countryCode={shipOrPickup?.country}
            addressyEnabled={!!shippingAddressConfig?.isAddressyEnabled}
            addressyKey={addressyApiKey}
            manualEntryItemLabel={textFor?.addressyManualEntry}
            errorTmnts={errorTmnts}
            onSelectAddress={onSelectAddress}
            isAPOFPO={shippingAddress?.isAPOFPO}
          />

          {userData?.isLoggedIn && (
            <Controller
              name="defaultShippingAddressOption"
              control={control}
              render={({ field }) => (
                <Checkbox
                  {...field}
                  description={textFor?.saveAsDefaultAddress?.value}
                  classList="save-as-default"
                  id="default-shipping-address-option"
                  data-testid="defaultShippingAddressOption"
                  isChecked={field.value}
                />
              )}
            />
          )}
        </>
      )}

      {visibleView === SHIPPING_VIEW.SAVED_ADDRESS && (
        <SavedAddress
          savedAddress={savedAddress}
          textFor={{
            change: textFor?.change,
          }}
          onChangeAddress={onChangeAddress}
          allowChangeAddress={!isPayPalExpressCheckout}
        />
      )}

      {openAddressSelectionModal
        && (
          <AddressSelectionModal
            addressData={checkoutPageState?.addressBook?.addresses}
            textFor={checkoutPageState?.textFor}
            shippingAddress={checkoutPageState?.shippingAddress}
            isOpen={openAddressSelectionModal}
            onAddNewAddress={onAddNewAddress}
            setModalVisibility={setModalVisibility}
            onUseThisAddress={onUseThisAddress}
            isNewAddressSelected={isNewAddressSelected}
          />
        )}
    </div>
  );
}
